import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  startIdleSessionMonitor,
  stopIdleSessionMonitor,
  markActiveNow,
} from '../../redux/authentication/utils/core/utils/idleSessionManager';

/**
 * Mounted once at the app root. Starts/stops the idle-session monitor based on
 * auth state, and renders a small "you'll be logged out soon" prompt for the
 * ~60 seconds before it actually happens -- being logged out with zero warning
 * reads as a bug, not a security feature.
 */
const IdleSessionWarning = () => {
  const { t } = useTranslation();
  const isAuthenticated = useSelector((state) => !!state.auth?.isAuthenticated);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const handleWarn = useCallback((seconds) => {
    setSecondsLeft(seconds);
  }, []);

  const handleWarnCancel = useCallback(() => {
    setSecondsLeft(null);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      stopIdleSessionMonitor();
      setSecondsLeft(null);
      return;
    }
    startIdleSessionMonitor({ onWarn: handleWarn, onWarnCancel: handleWarnCancel });
    return () => stopIdleSessionMonitor();
  }, [isAuthenticated, handleWarn, handleWarnCancel]);

  // Local countdown display only -- the actual logout decision is owned by
  // idleSessionManager's own interval, not this component's timer.
  useEffect(() => {
    if (secondsLeft === null) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => (s !== null && s > 0 ? s - 1 : s));
    }, 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const handleStayLoggedIn = () => {
    markActiveNow();
    setSecondsLeft(null);
  };

  if (secondsLeft === null) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[10000] max-w-sm rounded-2xl border border-amber-500/30 bg-white dark:bg-gray-900 shadow-xl p-5">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-xl bg-amber-500/15 flex-shrink-0">
          <Clock className="h-5 w-5 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 dark:text-white">
            {t('common.idleSession.title')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('common.idleSession.bodyPrefix')}{' '}
            <span className="font-medium text-gray-700 dark:text-gray-200 tabular-nums">
              {secondsLeft}s
            </span>{' '}
            {t('common.idleSession.bodySuffix')}
          </p>
          <button
            onClick={handleStayLoggedIn}
            className="mt-3 w-full px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            {t('common.idleSession.stayButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdleSessionWarning;
